import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import useFilters from "../../../hooks/useFilters";
import Select from "react-select";
import dayjs from "dayjs";
import PageHeader from "../../general/PageHeader";
import FiltersContainer from "../../general/FiltersContainer";
import Loader from "../../general/Loader";
import { formatCurrency, getLink, getOptionsForDropdown, getUsuariosForDropdown, getVendedoresForDropdown, getZonasForDropdown, toast, validarPINSeguridad } from "../../../utils/helpers";
import { postRequest, request } from "../../../utils/request";
import { Link } from "react-router-dom";
import Dropdown from "../../general/Dropdown";
import Pagination from "../../general/Pagination";
import Modal from "../../general/Modal";
import { calcAggregate } from "./functions";
import AvatarEmpleado from "../../general/AvatarEmpleado";
import { debounce } from "lodash";

const selectedReducer = (state, {type, payload}) => {
    if (state.loading && type !== "completeAllProcess" && type !== "reset") return state;

	switch (type) {
		case "add":
            if (state.documentos.some((documento) => documento.uid === payload.documento.uid)) {
                return state;
            }

			return {
				...state,
				documentos: [...state.documentos, payload.documento],
			};
        case "startAllProcess":
            return {
                ...state,
                loading: true,
                documentos: [],
                allSelected: true,
            }
        case "completeAllProcess":
            return state.loading ? {
                ...state,
                loading: false,
                documentos: [...payload.documentos],
                allSelected: true,
            } : { ...state };
		case "remove":
			const documentos = state.documentos.filter((documento) => documento.uid !== payload.documento.uid);
			return {
				...state,
				documentos,
                allSelected: false,
			};
        case "reset":
        default:
			return {
				documentos: [],
                allSelected: false,
                loading: false,
			};
	}
};

const CuadreCaja = () => {
    const [filters, setFilters] = useState({
        desde: "",
        hasta: dayjs().endOf("day").format("YYYY-MM-DD"),
    });
    const { handleFilterChange, filtersQuery, setFilters: setUseFilters } = useFilters(filters);
    const [selected, setSelected] = useReducer(selectedReducer, {
        documentos: [],
        allSelected: false,
    });
    const [showDocumentos, setShowDocumentos] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [cuadrandoTodos, setCuadrandoTodos] = useState(false);
    const [documentos, setDocumentos] = useState({ data: [] });
    const [cuadres, setCuadres] = useState({ data: [] });
    const [zonas, setZonas] = useState([]);
    const [vendedores, setVendedores] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [tipos, setTipos] = useState([]);
	const [numeraciones, setNumeraciones] = useState([]);
    const [showCuadrarTodosModal, setShowCuadrarTodosModal] = useState(false);
    const [pinSeguridad, setPinSeguridad] = useState("");

    const debounceAll = useCallback(debounce(async (cancel = false) => {
        if (cancel) return;

        const limit = 5000;
        if(documentos?.meta?.total > limit) {
            toast(`Se han agregado solo los primeros ${limit} documentos. Para un cuadre con la totalidad de los documentos favor usar el botón cuadrar todos`, 'warning');
        };

        try {
            const requestLimit = 1000;
            const response = { data: [] };
            if (documentos?.meta?.total > requestLimit) {
                const length = Math.ceil(Math.min(limit, documentos?.meta?.total) / requestLimit);

                const responses = await Promise.all(Array.from({ length }, (_, index) => request(`documentos-cobros${filtersQuery}&limit=${requestLimit}&page=${index + 1}`)));

                response.data = responses.map(r => r.data).flat();
            } else {
                response.data = (await request(`documentos-cobros${filtersQuery}&limit=${requestLimit}`)).data;
            }
            
            setSelected({ type: "completeAllProcess", payload: { documentos: response.data } });
        } catch (error) {
            toast('Error al seleccionar todos los documentos. Favor intentar de nuevo.', 'danger');
            setSelected({ type: "reset" });
            console.error(error);
        }
    }, 1000), [filtersQuery, documentos]);

    const handleAll = async (checked) => {
        if (checked) {
            if (selected.loading) return;

            setSelected({ type: "startAllProcess" });

            debounceAll();
        } else {
            debounceAll(true); // Cancelar las llamadas a api porque ya no se necesita
            setSelected({ type: "reset" });
        }
    };

    const handleSelect = (documento, checked) => {
        if (checked) {
            setSelected({ type: "add", payload: { documento } });
        } else {
            setSelected({ type: "remove", payload: { documento } });
        }
    };

    const onFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
        handleFilterChange(field, value);
    };

    const openCuadrarTodosModal = () => {
        setPinSeguridad("");
        setShowCuadrarTodosModal(true);
    };

    const closeCuadrarTodosModal = () => {
        setPinSeguridad("");
        setShowCuadrarTodosModal(false);
    };

    const cuadrarTodos = async () => {
        setCuadrandoTodos(true);

        if (await validarPINSeguridad(pinSeguridad)) {
            const { desde, hasta } = filters;
            const response = await postRequest(`cuadre-caja/todos?${desde ? `desde=${desde}` : ''}${desde && hasta ? '&' : ''}${hasta ? `hasta=${hasta}` : ''}`);

            if (response.success) {
                setPinSeguridad("");

                toast("Todos los documentos cuadrados", "success");

                closeCuadrarTodosModal();

                // await getData(`documentos-cobros${filtersQuery}`);
                setShowDocumentos(false);
            } else {
                toast(response.data.message, "error");
            }
        } else {
            toast("PIN incorrecto, intente de nuevo", "error");
        }

        setCuadrandoTodos(false);
    };

    const getOptions = async () => {
        setTipos([
            { value: "", label: "Todos" },
            { value: "Factura", label: "Factura" },
            { value: "Recibo", label: "Recibo" },
            { value: "Nota de crédito", label: "Nota de crédito" },
        ]);

        const [zonaData, vendedorData, usuarioData, numeracionFactura] = await Promise.all([
            getZonasForDropdown(),
            getVendedoresForDropdown(),
            getUsuariosForDropdown(),
            getOptionsForDropdown('numeracion-documentos?tipo_documento=factura_venta'),
        ]);

        const [todas, ...zonas] = zonaData;
        setZonas([todas, { value: '0', label: "Sin Zona" }, ...zonas]);
        const [todos, ...vendedores] = vendedorData;
        setVendedores([todos, { value: '0', label: "Sin Vendedor" }, ...vendedores]);

        setUsuarios(usuarioData);
        setNumeraciones([
            { value: '', label: 'Todas' },
            ...numeracionFactura,
        ]);
    };

    const getData = async (url) => {
        setIsLoading(true);
        setDocumentos({ data: [] });

        const base = showDocumentos ? 'documentos-cobros' : 'cuadre-caja';
        url = url ? url + "&limit=15" : `${base}?limit=15`;
        let response = { data: [] };

        try {
            response = await request(url);
        } catch (error) {
            console.error(error);
        }

        if(showDocumentos) {
            setDocumentos(response);
        }
        else setCuadres(response);

        setIsLoading(false);
    };

    useEffect(() => {
        const url = showDocumentos ? 'documentos-cobros' : 'cuadre-caja';
        getData(`${url}${filtersQuery}`);
    }, [filtersQuery]);

    useEffect(() => {
        setSelected({ type: "reset" });
        setDocumentos({ data: [] });
        setCuadres({ data: [] });

        const filters = {
            desde: showDocumentos ? "" : dayjs().startOf("day").format("YYYY-MM-DD"),
            hasta: dayjs().endOf("day").format("YYYY-MM-DD"),
            documento: showDocumentos ? "1" : null,
            buscar: "",
        }
        setFilters(filters);
        setUseFilters(filters);
    }, [showDocumentos]);

    useEffect(() => setPinSeguridad(""), [showCuadrarTodosModal]);
        
    useEffect(() => {
        getOptions();
    }, []);

    return <>
        <PageHeader hasButton={false}>
            <div className="btn-group d-none d-lg-flex w-100 mt-2" role="group">
                <div className="col-12 col-md d-flex align-items-center">
                    <div className="mr-3 d-flex flex-column justify-content-center" style={{gap: '0.05rem'}}>
                        <h2 className="m-0 p-0" style={{border: 'none', color: '#444', fontWeight: 500}}>Cuadre de caja</h2>
                        <small className="text-muted">Resumen y detalle de movimientos de caja</small>
                    </div>

                    <button
                        type="button"
                        className={`btn btn-outline-info py-2 ${
                            showDocumentos ? "active" : ""
                        }`}
                        style={{borderRadius: '6px'}}
                        onClick={() => {
                            setShowDocumentos(true);
                        }}
                    >
                        <i className="fas fa-plus mx-2"></i> Nuevo Cuadre
                    </button>

                    <button
                        type="button"
                        className={`btn btn-outline-info py-2 mx-2 ${
                            showDocumentos ? "" : "active"
                        }`}
                        style={{borderRadius: '6px'}}
                        onClick={() => {
                            setShowDocumentos(false);
                        }}
                    >
                        <i className="fas fa-history mx-2"></i> Historial Cuadre
                    </button>

                    {showDocumentos && !!documentos.data?.length && <button
                        className="btn btn-outline-info py-2 tooltip-self"
                        onClick={() => openCuadrarTodosModal()}
                    >
                        <i className="fas fa-file-signature"></i>
                        <span className="tootip-text tooltip-right">Cuadrar todos</span>
                    </button>}
                </div>
                
                <div className="col-12 col-md-4 row pretty-form">
                    <div className="col-12 col-md form-group">
                        <label
                            htmlFor="desde"
                            style={{ fontSize: "12px", textTransform: "uppercase" }}
                        >
                            Desde
                        </label>
                        <input
                            type="date"
                            name="desde"
                            id="desde"
                            className="form-control"
                            value={filters.desde}
                            onChange={(e) =>
                                onFilterChange("desde", e.target.value)
                            }
                        />
                    </div>
                    <div className="col-12 col-md form-group">
                        <label
                            htmlFor="hasta"
                            style={{ fontSize: "12px", textTransform: "uppercase" }}
                        >
                            Hasta
                        </label>
                        <input
                            type="date"
                            name="hasta"
                            id="hasta"
                            className="form-control"
                            value={filters.hasta}
                            onChange={(e) =>
                                onFilterChange("hasta", e.target.value)
                            }
                        />
                    </div>
                </div>
            </div>
        </PageHeader>

        {showDocumentos && <Cintillo {...(selected.documentos?.length ? calcAggregate(selected.documentos) : documentos.aggregate )} />}

        <FiltersContainer className="pretty-form">
            <div className="col form-group">
                <label className="orden-filter-label">BUSCAR</label>
                <input
                    value={filters.buscar}
                    onChange={(e) => onFilterChange("buscar", e.target.value)}
                    type="search"
                    placeholder="Buscar..."
                    className="form-control"
                    name="buscar"
                />
            </div>

            <div className="col-12 col-md-6 col-lg-2 form-group">
                <label>VENDEDOR</label>
                <Select
                    id="vendedor"
                    options={vendedores}
                    name="vendedor"
                    placeholder="Todos"
                    value={vendedores.find((vendedor) => vendedor.value === filters.vendedor) ?? null}
                    onChange={(e) => onFilterChange("vendedor", e.value)}
                />
            </div>

            {showDocumentos && (<>
                <div className="col-12 col-md-6 col-lg-2 form-group">
                    <label>USUARIO</label>
                    <Select
                        id="usuario"
                        options={usuarios}
                        name="usuario"
                        placeholder="Todos"
                        value={usuarios.find((usuario) => usuario.value === filters.usuario) ?? null}
                        onChange={(e) => onFilterChange("usuario", e.value)}
                    />
                </div>

                <div className="col-12 col-md-6 col-lg-2 form-group">
                    <label className="orden-filter-label">ZONA</label>
                    <Select
                        id="zona"
                        options={zonas}
                        name="zona"
                        placeholder="Todas"
                        value={zonas.find((zona) => zona.value == filters.zona) ?? null}
                        onChange={(e) => onFilterChange("zona", e.value)}
                    />
                </div>

                <div className="col-12 col-md-6 col-lg-2 form-group">
                    <label className="orden-filter-label">TIPO DE DOCUMENTO</label>
                    <Select
                        id="tipo"
                        options={tipos}
                        name="tipo"
                        placeholder="Todos"
                        value={tipos.find((tipo) => tipo.value === filters.tipo) ?? null}
                        onChange={(e) => onFilterChange("tipo", e.value)}
                    />
                </div>

                <div className="col-12 col-md-6 col-lg-2 form-group">
                    <label className="orden-filter-label">NUMERACIÓN</label>
                    <Select
                        id="numeracion"
                        options={numeraciones}
                        name="numeracion"
                        placeholder="Todas"
                        value={numeraciones.find((numeracion) => numeracion.value === filters.numeracion) ?? null}
                        onChange={(e) => onFilterChange("numeracion", e.value)}
                    />
                </div>
            </>)}
        </FiltersContainer>

        <div
			className="table-data table-responsive table-row__desktop"
			style={{ overflowX: "visible" }}
		>
            {isLoading && <Loader />}

            {!isLoading && (showDocumentos
                ? <DocumentosTable
                    documentos={documentos}
                    selected={selected}
                    handleSelect={handleSelect}
                    handleAll={handleAll}
                    getDocumentos={getData}
                    setSelected={setSelected}
                    setShowDocumentos={setShowDocumentos}
                    filtersQuery={filtersQuery}
                    filters={filters}
                />
                : <CuadreTable cuadres={cuadres} getCuadres={getData} />
            )}
        </div>

        <Modal
            title="Cuadrar todos los documentos"
            show={showCuadrarTodosModal}
            blank={false}
            callback={cuadrarTodos}
            toggle={closeCuadrarTodosModal}>
                {
                    cuadrandoTodos 
                    ? <><Loader /></> 
                    : <>
                        <p className="px-3 pb-3">¿Estás seguro que desea cuadrar todos los documentos? (Esta acción no se puede deshacer).</p>
                
                        <div className="form-group">
                            <label>PIN de seguridad</label>
                            <input
                                type="password"
                                className="form-control"
                                id="PIN"
                                name="pin_seguridad"
                                value={pinSeguridad}
                                onChange={(e) =>
                                    setPinSeguridad(e.target.value)
                                }
                            />
                        </div>
                    </>
                }
        </Modal>
    </>;
};

const DocumentosTable = ({documentos, selected, handleSelect, handleAll, getDocumentos, setSelected, setShowDocumentos, filtersQuery, filters}) => {
    const [showFormModal, setShowFormModal] = useState(false);

    return <>
        <table className="table table-data2">
            <thead>
                <tr>
                    <th>
                        <input
                            className="form-check-input"
                            type="checkbox"
                            checked={selected.allSelected}
                            onChange={(e) => handleAll(e.target.checked)} />
                        Documento
                    </th>
                    <th>Contacto</th>
                    <th>Fecha</th>
                    <th>Referencia</th>
                    {/* <th>Tipo Documento</th> */}
                    <th>Vendedor</th>
                    <th>Zona</th>
                    <th>Monto Pagado</th>
                    <th>Tipo de Pago</th>
                    <th>Usuario</th>
                    <th></th>
                </tr>
            </thead>

            <tbody>
                {documentos.data.map((documento) => {
                    const isSelected = (selected.allSelected && selected.loading) || selected.documentos?.some((d) => d.uid === documento.uid);

                    return <tr className="tr-shadow" key={documento.uid}>
                        <td>
                            <div className="form-check d-flex flex-column">
                                {!documento.cuadre_caja_id && <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => handleSelect(documento, e.target.checked)}
                                />}
                                <strong
                                    className="form-check-label"
                                    htmlFor="defaultCheck1"
                                >
                                    {documento.numero_comprobante}
                                </strong>
                                <span className={[
                                    'badge',
                                    'badge-light',
                                    documento.tipo === "Factura" ? 'badge-info' : '',
                                    documento.tipo === "Recibo" ? 'badge-success' : '',
                                    documento.tipo === "Nota de crédito" ? 'badge-warning' : '',
                                ].join(' ')}>{documento.tipo}</span>
                            </div>
                        </td>
                        <td>
                            <Link
                                to={getLink(`/contactos/${documento.contacto_id}/transacciones`)}
                                className="ml-2 contacto"
                                onClick={() => {localStorage.setItem("contacto", JSON.stringify(documento.cliente));}}
                            >
                                {documento.cliente?.codigo} - {documento.cliente?.nombre}
                            </Link>
                        </td>
                        <td>{dayjs(documento.fecha).format("DD/MM/YYYY" )}</td>
                        <td>{documento.referencia}</td>
                         <td style={{width: '250px'}}>
                            {documento.vendedor
                                ? <AvatarEmpleado empleado={documento.vendedor} />
                                : ''}
                        </td>
                        <td>
                            {documento.ruta && <>
                                {/* <i class="fas fa-map-marker-alt text-success mr-2"></i> */}
                                {documento.ruta?.nombre}
                            </>}
                        </td>
                        <td style={{fontWeight: 600}}>{formatCurrency(documento.total, "$")}</td>
                        <td className="text-capitalize"><span className="badge badge-info">{documento.tipo_pago}</span></td>
                        <td>{documento.usuario ? `${documento.usuario.nombre} ${documento.usuario.apellido}` : '--'}</td>

                        <td>
                            <Dropdown btnText=""
                                btnIcon={<i className="zmdi zmdi-more-vert"></i>}
                                styles={{
                                    dropdownMenu: {
                                        left: "-250%",
                                    },
                                }}>
                                <Link
                                    to={getLink(documento.tipo === "Factura"
                                        ? `/imprimir/facturas/${documento.id}`
                                        : documento.tipo === "Recibo"
                                            ? `/imprimir/recibos-ingreso/${documento.id}`
                                            : `/imprimir/notas-credito/${documento.id}`
                                    )}
                                    target="_blank"
                                    className="dropdown-item"
                                    title="Impresión normal"
                                >
                                    Imprimir {documento.tipo}
                                </Link>
                                {documento.cuadre_caja_id && <>
                                    <Link
                                        to={getLink(`/imprimir/cuadre-caja/${documento.cuadre_caja_id}`)}
                                        target="_blank"
                                        className="dropdown-item"
                                        title="Impresión normal"
                                    >
                                        Imprimir Cuadre
                                    </Link>
                                </>}
                            </Dropdown>
                        </td>
                    </tr>
                })}
            </tbody>
        </table>
    
        {documentos.links && <Pagination
            links={documentos.links}
            meta={documentos.meta}
            getData={getDocumentos}
        />}

        <Modal
            show={showFormModal}
            size="70%"
            blank={true}
            toggle={() => {
                setShowFormModal(false);
            }}
        >
            <Form
                documentos={selected.documentos}
                loadingDocumentos={selected.loading}
                onSuccess={() => {
                    setSelected({ type: "reset" });
                    // getDocumentos(`documentos-cobros${filtersQuery}`);
                    setShowFormModal(false);
                    setShowDocumentos(false);
                }}
                onCancel={() => {
                    setShowFormModal(false);
                }}
            />
        </Modal>

        {(selected.documentos?.length > 0 || selected.allSelected) && (
            <button
                className="btn btn-info"
                onClick={() => setShowFormModal(true)}
                style={{ position: "fixed", bottom: "20px", left: "50%", transform: "translateX(-50%)", zIndex: 1000 }}
            >
                + CUADRAR CAJA
            </button>
        )}
    </>;
};

const CuadreTable = ({cuadres, getCuadres}) => {
    return <>
        <table className="table table-data2">
            <thead>
                <tr>
                    <th>Codigo</th>
                    <th>Fecha</th>
                    <th>Vendedor</th>
                    <th>Cantidad de Documento</th>
                    <th>Total</th>
                    <th>Estado</th>
                    <th></th>
                </tr>
            </thead>

            <tbody>
                {cuadres.data?.map((cuadre) => {
                     return <tr className="tr-shadow" key={cuadre.id}>
                        <td>{cuadre.codigo_cuadre}</td>
                        <td>{dayjs(cuadre.fecha_cuadre).format("DD/MM/YYYY")}</td>
                        <td>{cuadre.vendedor?.nombre ?? '--'}</td>
                        <td>{cuadre.cantidad_documentos}</td>
                        <td>{formatCurrency(cuadre.total)}</td>
                        <td className="text-capitalize">{cuadre.estado}</td>
                        <td>
                            <Dropdown btnText="Acciones">
                                <Link
                                    to={getLink(`/imprimir/ingresos/cuadre-caja?id=${cuadre.id}`)}
                                    target="_blank"
                                    className="dropdown-item"
                                >
                                    Cuadre de caja
                                </Link>
                                <Link
                                    to={getLink(
                                        `/imprimir/ingresos/cuadre-caja/resumen?id=${cuadre.id}`
                                    )}
                                    target="_blank"
                                    className="dropdown-item"
                                >
                                    Cuadre de caja de Cobros
                                </Link>
                            </Dropdown>
                        </td>
                    </tr>
                })}
            </tbody>
        </table>
    
        {cuadres.links && <Pagination
            links={cuadres.links}
            meta={cuadres.meta}
            getData={getCuadres}
        />}
    </>;
};

const DocumentosForm = ({documentos, titulo, icon, severity}) => {
    return <>
        <div className="d-flex w-100" style={{gap: '0.5rem'}}>
            <div className={`badge badge-${severity} badge-light my-auto p-2`} style={{fontSize: '0.8rem'}}>
                <i className={icon}></i>
            </div>

            <span className={`my-auto text-${severity} mr-auto`} style={{fontSize: '1rem', fontWeight: 600}}>{titulo}</span>
        
            <div className={`badge badge-${severity} badge-light my-auto p-2`} style={{fontSize: '0.7rem'}}>
                {documentos.length} {documentos.length === 1 ? "documento" : "documentos"}
            </div>
        </div>

        <table className="table table-data mt-2 pretty-table">
            <thead>
                <tr>
                    <th className="text-center" style={{width: '50px'}}>#</th>
                    <th style={{maxWidth: '150px'}}>Documento</th>
                    <th>Contacto</th>
                    <th style={{maxWidth: '150px'}}>Total</th>
                </tr>
            </thead>

            <tbody>
                {documentos.map((documento, index) => <tr>
                    <td className={`text-center text-${severity}`}>
                        <input type="hidden" name={`documentos[${index}][id]`} value={documento.id} />
                        <input type="hidden" name={`documentos[${index}][tipo]`} value={documento.tipo} />
                        
                        <span className="font-weight-bold">{index + 1}</span>
                    </td>

                    <td>{documento.numero_comprobante}</td>
                    <td style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                        {documento.cliente?.codigo} - {documento.cliente?.nombre}
                    </td>
                    <td>{formatCurrency(documento.total)}</td>
                </tr>)}
            </tbody>
        </table>
    </>;
};

const Form = ({documentos = [], loadingDocumentos, onSuccess, onCancel}) => {
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [errors, setErrors] = useState({});
    const formRef = useRef(null);

    const regenerarCuadre = async (e) => {
        e.preventDefault();

        if(loading) return;

        setLoading(true);

        const formData = new FormData(e.target);

        const res = await postRequest("cuadre-caja", formData);
        if(res.data.id) {
            // window.open(getLink(`/imprimir/ingresos/cuadre-caja?id=${res.data.id}`));
        
            onSuccess();
        } else {
            toast("Error al generar el cuadre de caja", "error");
        }

        setLoading(false);
    };

    const validateForm = () => {
        const errors = {};
        let validado = true;
        const formData = new FormData(formRef.current);

        if(!formData.get("fecha")) {
            errors.fecha = "Por favor, seleccione una fecha de entrega";
            validado = false;
        }

        setErrors(errors);
        return validado;
    }

    const openModal = () => {
        const valido = validateForm();
        if (valido) setShowModal(true);
    };

    const acceptModal = () => {
        setShowModal(false);

        formRef.current.requestSubmit();
    }


    const facturas = useMemo(() => documentos?.filter((documento) => documento.tipo === "Factura"), [documentos]);
    const recibos = useMemo(() => documentos?.filter((documento) => documento.tipo === "Recibo"), [documentos]);
    const notas = useMemo(() => documentos?.filter((documento) => documento.tipo === "Nota Crédito"), [documentos]);

    return <>
        <form ref={formRef} onSubmit={regenerarCuadre}>
            <div className='popup__header'>
                <div className="d-flex w-100 m-0 py-2 px-3">
                    <div className="my-auto badge badge-info badge-light p-2 mr-2" style={{fontSize: '1.25rem'}}>
                        <i className="fas fa-archive"></i>
                    </div>
                    
                    <div className="my-auto" style={{lineHeight: 1}}>
                        <h4 className="m-0">Generar Cuadre de Caja</h4>
                        <small className="text-muted" style={{fontSize: '10px'}}>Resumen de pagos y documentos</small>
                    </div>


                    <div className='ml-auto pretty-form' style={{width: '256px', fontSize: '12px'}}>
                        <div className='form-group mb-0'>
                            <label className="text-info">
                                <i className="fas fa-calendar mr-1"></i>
                                Fecha de cuadre
                            </label>
                            <input
                                type='datetime-local'
                                name='fecha'
                                id='fecha'
                                className='form-control'
                                defaultValue={dayjs().format("YYYY-MM-DD HH:mm")}
                                style={{padding: '0.25rem 0.75rem', fontSize: '14px'}}
                            />
                            {errors.fecha && (
                                <small className='help-blockParams form-Text'>
                                    {errors.fecha}
                                </small>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            <div className='popup__body pt-2'>
                {!loadingDocumentos && <CintilloModal {...calcAggregate(documentos ?? [])} />}

                <div className="row mt-3">
                    <div className="col-12">
                        {loadingDocumentos
                            ? <Loader size="sm" />
                            : <>
                                {facturas?.length > 0 && <DocumentosForm severity="info" documentos={facturas} titulo="Facturas" icon="far fa-file-alt" />}
                                {recibos?.length > 0 && <DocumentosForm severity="success" documentos={recibos} titulo="Recibos" icon="far fa-file-alt" />}
                                {notas?.length > 0 && <DocumentosForm severity="warning" documentos={notas} titulo="Notas de Crédito" icon="far fa-file-alt" />}
                            </>
                        }
                    </div>
                </div>
            </div>

            <div className='popup__footer'>
                <button type='button' className='btn btn-plain' onClick={onCancel} disabled={loading}>
                    Cancelar
                </button>
                <button type='button' className='btn btn-primary' disabled={loading || loadingDocumentos} onClick={openModal}>
                    {loading ? "Generando..." : <>
                        <i className="fas fa-calculator mr-2"></i>
                        Generar Cuadre
                    </>}
                </button>
            </div>
        </form>

        <Modal
            show={showModal}
            blank={false}
            size={"600px"}
            toggle={() => {
                setShowModal(false);
            }}
            callback={acceptModal}
        >
            Se agregaran {documentos?.length} documentos al cuadre. Esta acción no podrá revertirse. ¿Está seguro de querer crear el cuadre?
        </Modal>
    </>;
};

const CintilloSingleCard = ({value, label, className = 'col px-1', style = {}, fontSize = '14px'}) => {
    return <div className={className} style={style}>
        <span className="bold h5" style={{fontSize}}>{formatCurrency(value)}</span><br />
        <span style={{margin: '4px 0px', textTransform: 'uppercase', fontWeight: 500, fontSize: '12px', color: '#777'}}>{label}</span>
    </div>;
};

const CintilloCard = ({severity, title, icon, children, col = 'col-md'}) => {
    return <div className={`col-12 ${col} p-0 pl-1 pb-1 bg-${severity}`} style={{borderRadius: '15px'}}>
            <div className="card m-0" style={{fontSize: "14px", borderRadius: '15px', borderTopRightRadius: 0}}>
                <div className="card-title w-100 my-2 d-flex" style={{fontWeight: 600, color: '#444'}}>
                    <span className={`badge badge-light badge-${severity} mx-2 my-auto p-2`}>
                        <i className={icon} style={{fontSize: '1.25rem'}}></i>
                    </span>
                    <span className="h5 my-auto">{title}</span>
                </div>
                <div className={`card-body py-1 mb-2 text-${severity}`} style={{borderTop: '1px solid #dfdfdf'}}>
                    {children}
                </div>
            </div>
        </div>;
};

const Cintillo = ({recibos, facturas, notaCreditos, efectivo, tarjeta, transferencia, deposito, cheque, otro, contado, credito, contra_entrega, total_facturado}) => {
    return <div className="row mb-4" style={{gap: '1rem'}}>
        <CintilloCard severity="info" title="DETALLES DE CUADRE" icon="far fa-file-alt" col="col-md-4">
            <div className="row my-2">
                <CintilloSingleCard className="col-6 px-1" value={recibos + facturas} label="Total Ingresos" />
                <CintilloSingleCard className="col-6 px-1" value={total_facturado} label="Total Facturado" />
            </div>

            <div className="row my-2">
                <CintilloSingleCard value={recibos} label="Recibos" />
                <CintilloSingleCard value={facturas} label="Facturas" />
                <CintilloSingleCard value={notaCreditos} label="Notas Crédito" />                       
            </div>
        </CintilloCard>

        <CintilloCard severity="success" title="FORMAS DE PAGO" icon="fas fa-wallet" col="col-md">
            <div className="row my-2">
                <CintilloSingleCard value={efectivo} label="Efectivo" />
                <CintilloSingleCard value={tarjeta} label="Tarjeta" />
                <CintilloSingleCard value={transferencia} label="Transferencia" />
            </div>

            <div className="row my-2">
                <CintilloSingleCard value={deposito} label="Depósito" />
                <CintilloSingleCard value={cheque} label="Cheque" />
                <CintilloSingleCard value={otro} label="Otro" />
            </div>
        </CintilloCard>
        
        <CintilloCard severity="help" title="TÉRMINOS DE PAGO" icon="fas fa-cash-register" col="col-md-3">
            <div className="row my-2">
                <CintilloSingleCard className="col-12 px-1" value={contado} label="Contado" />
            </div>

            <div className="row my-2">
                <CintilloSingleCard value={credito} label="Crédito" />
                <CintilloSingleCard value={contra_entrega} label="Contra Entrega" />
            </div>
        </CintilloCard>
    </div>;
}


const CintilloModalSingleCard = ({value, label, icon, severity}) => {
    return <div className="col my-auto">
        <div className={`bg-${severity} pb-1 rounded shadow-sm`}>
            <div className="py-2 px-3 bg-white d-flex flex-column rounded">
                <div className={`badge badge-${severity} badge-light p-2 mx-auto mt-2`} style={{fontSize: '1.5rem'}}>
                    <i className={`${icon} mx-auto text-${severity}`}></i>
                </div>

                <span className={`bold mt-3 mx-auto text-${severity}`} style={{fontSize: '0.9rem'}}>{formatCurrency(value)}</span>
                
                <small className="mx-auto mb-2 text-muted" style={{textTransform: 'uppercase', fontSize: '0.7rem'}}>{label}</small>
            </div>
        </div>
    </div>;
};

const CintilloModal = ({efectivo, tarjeta, transferencia, deposito, cheque, otro}) => {
    return <>
        <div className="row">
            <div className={'col-12 col-md-12 px-1 mx-auto'}>
                <div className="card" style={{fontSize: "14px"}}>
                    <div className="card-body  py-1 my-2">
                        <div className="row w-100 mt-2 mb-4" style={{gap: '0.5rem'}}>
                            <div className={`badge badge-info badge-light my-auto p-2 mx-2`} style={{fontSize: '0.8rem'}}>
                                <i className="fas fa-wallet"></i>
                            </div>

                            <span className={`my-auto text-dark mr-auto`} style={{fontSize: '1rem', fontWeight: 700}}>FORMAS DE PAGO</span>
                        </div>

                        <div className="row">
                            <CintilloModalSingleCard severity="success" icon="fas fa-money-bill-alt" value={efectivo} label="Efectivo" />
                            <CintilloModalSingleCard severity="help" icon="fas fa-credit-card" value={tarjeta} label="Tarjeta" />
                            <CintilloModalSingleCard severity="primary" icon="fas fa-exchange-alt" value={transferencia} label="Transferencia" />
                            <CintilloModalSingleCard severity="warning" icon="fas fa-university" value={deposito} label="Depósito" />
                            <CintilloModalSingleCard severity="info" icon="fas fa-check-circle" value={cheque} label="Cheque" />
                            <CintilloModalSingleCard severity="dark" icon="fas fa-ellipsis-h" value={otro} label="Otro" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </>;
};

export default CuadreCaja;
